import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  CASE_IMAGE_JOB_STATUS_KEY,
  CASE_IMAGE_VARIANT_KIND_KEY,
  CASE_IMAGE_VARIANT_POLL_INTERVAL_MS,
} from '@messenger/consts'
import type { CaseImageVariantListItem } from '@messenger/types'
import {
  isTransientPollError,
  MAX_CONSECUTIVE_POLL_ERRORS,
} from '@/api/caseImageJobPolling'
import {
  acceptCaseImageVariant,
  getCaseImageVariantPricing,
  getCaseImageVariantStatus,
  getCaseImageVariantsForImage,
  processCaseImageVariant,
} from '@/api/caseImageVariants'
import {
  hasDismissedBackgroundRemovalDisclosure,
  rememberBackgroundRemovalDisclosureDismissed,
} from '@/utils/editor/backgroundRemovalDisclosure'
import { imagePath } from '@/utils/editor/resolveCaseImageForItem'
import {
  isAiImageEditsDisabledError,
  mayHaveCommitted,
} from '@/utils/caseImages/variantErrorCodes'

/**
 * After this long in the removing state the copy switches to "still
 * working": the model endpoint scales to zero, and the worker waits several
 * minutes for it to wake (BACKGROUND_REMOVAL_WAKE_BUDGET_MS in the backend),
 * so a wait past this point is expected, not a failure. Polling
 * itself never gives up on time; only a terminal row or a dead request
 * path ends it, because a client-side timeout could abandon a generation
 * that completes seconds later.
 */
export const BACKGROUND_REMOVAL_SLOW_AFTER_MS = 60_000

/**
 * The screens of the Remove Background tool, in the order a person meets
 * them. `idle` means the tool is closed and the normal Pintura editor shows.
 * A paid removal starts from `disclosure` the first time and from `start`
 * once the disclosure was dismissed; both show the price before anything
 * runs.
 */
export type BackgroundRemovalStep =
  | 'idle'
  | 'applied'
  | 'loading'
  | 'previous'
  | 'disclosure'
  | 'start'
  | 'removing'
  | 'review'
  | 'accepting'
  | 'applying'
  | 'accepted'
  | 'applyFailed'
  | 'discarded'
  | 'error'

/** A generated cutout the person can apply to the editor item. */
export type BackgroundRemovalResult = {
  variantId: string
  url: string
}

export type BackgroundRemovalState = {
  step: BackgroundRemovalStep
  /** The generation currently being polled, reviewed or applied. */
  result: BackgroundRemovalResult | null
  /** An already-accepted cutout for this image, re-applicable for free. */
  previous: BackgroundRemovalResult | null
  /** The cutout the item already shows, on the `applied` screen. */
  applied: BackgroundRemovalResult | null
  /** True when the applied result was the free re-apply, so no charge is shown. */
  appliedPrevious: boolean
  /**
   * Price of one accepted removal in cents, as this open's pricing call (or
   * Try again's, or the accept's price check) answered it; null until it
   * answers, and after it fails.
   */
  priceCents: number | null
  /** True when this open's pricing call failed. */
  priceError: boolean
  /**
   * True when the price checked on accept differed from the one the review
   * quoted: nothing was billed, `priceCents` holds the new amount, and the
   * accept control says the price changed before it is pressed again.
   */
  priceChanged: boolean
  /** True once the current generation has run longer than the cold-start budget. */
  slow: boolean
  /**
   * Why the generation failed, kept for logging: a local reason, or the
   * failed row's errorCode. The UI shows neutral copy.
   */
  errorMessage: string | null
  /**
   * True when the error is the backend refusing because AI image edits are
   * switched off: the panel says so and offers no "Try again", since asking
   * again cannot help and nothing was started or billed.
   */
  disabled: boolean
}

type UseBackgroundRemovalArgs = {
  /** The CaseImage row the item shows; null hides the tool. */
  caseImageId: string | null
  /**
   * The photo the editor is showing for that row (no cache-bust query). When
   * it is an accepted enhancement, the cutout is made from it instead of the
   * smaller file that was uploaded.
   */
  imageUrl: string
  /** Scopes the "disclosure already seen" memory to the signed-in user. */
  userId: string | null | undefined
  /**
   * The accepted cutout the item already shows, if any. Opening the tool on
   * such an item shows it (the `applied` step) with the way back to the
   * original photo, instead of offering to remove a background that is gone.
   */
  appliedCutout?: BackgroundRemovalResult | null
  /**
   * Writes the accepted cutout onto the editor item. Resolves true once the
   * item holds it, and false when the write failed (the caller has already
   * shown why); the tool says "accepted" only after true.
   */
  onApply: (result: BackgroundRemovalResult) => Promise<boolean>
  /** Reports a failed accept call (the result stays reviewable). */
  onAcceptError?: (error: unknown) => void
}

export type UseBackgroundRemovalResult = BackgroundRemovalState & {
  /**
   * Open the tool: on an item that already shows a cutout, show it (the
   * `applied` step); otherwise fetch pricing, look up previous results, then
   * show the disclosure, or the priced start screen once it was dismissed.
   */
  open: () => Promise<void>
  /**
   * The person pressed the priced start on the disclosure or the start
   * screen; start generating. Does nothing on any other screen, nor until
   * the price is known, so a paid removal never starts unpriced.
   */
  confirmStart: () => void
  /** Ask for the price again after the pricing call failed. */
  retryPricing: () => void
  /**
   * The person kept the result: check the price is still the one on screen,
   * then bill it and apply it to the item. Does nothing while no price is
   * known, since the accept control states it; a changed price bills
   * nothing and shows the new amount instead.
   */
  accept: () => Promise<void>
  /** The person rejected the result: nothing billed, original stays. */
  discard: () => void
  /**
   * After a failure, go back to the priced start screen with a fresh price,
   * or repeat the lookup when that is what failed. Nothing starts until the
   * person confirms the price again.
   */
  tryAgain: () => void
  /** Apply the previously accepted cutout without a new generation. */
  applyPrevious: () => Promise<void>
  /**
   * After the cutout could not be written onto the item, write it again. The
   * cutout is already accepted, so this never bills.
   */
  retryApply: () => Promise<void>
  /** Close the tool and stop any polling. */
  close: () => void
}

const initialState: BackgroundRemovalState = {
  step: 'idle',
  result: null,
  previous: null,
  applied: null,
  appliedPrevious: false,
  priceCents: null,
  priceError: false,
  priceChanged: false,
  slow: false,
  errorMessage: null,
  disabled: false,
}

/**
 * The enhancement (an UPSCALE variant) the editor is showing, if any, so the
 * cutout is made from the photo the person is actually looking at.
 *
 * Without this the backend cuts out the base image (the uploaded file or its
 * MIO copy), which is often a fraction of the enhancement's size: a photo
 * enhanced to 2496 px came back as a 72 px cutout, so the result looked
 * broken next to the sharp photo it was meant to replace. Returns null when
 * the editor is showing the base image, which is the backend's default
 * source.
 */
export const sourceVariantForImage = (
  variants: CaseImageVariantListItem[],
  imageUrl: string,
): string | null => {
  if (!imageUrl) return null
  const shown = imagePath(imageUrl)
  const match = variants.find(
    (variant) =>
      variant.kind === CASE_IMAGE_VARIANT_KIND_KEY.UPSCALE &&
      variant.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED &&
      Boolean(variant.url) &&
      imagePath(variant.url as string) === shown,
  )
  return match?.id ?? null
}

/**
 * An already-accepted cutout that can be re-applied for free. It must have
 * been made from the same source as the photo on screen: a cutout of the
 * small uploaded file is not a cutout of its enhancement, and re-applying it
 * would swap the sharp photo for a blurry one.
 */
export const findPreviousAcceptedCutout = (
  variants: CaseImageVariantListItem[],
  sourceVariantId: string | null,
): BackgroundRemovalResult | null => {
  const match = variants.find(
    (variant) =>
      variant.kind === CASE_IMAGE_VARIANT_KIND_KEY.BG_REMOVAL &&
      variant.accepted &&
      variant.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED &&
      Boolean(variant.url) &&
      (variant.sourceVariantId ?? null) === sourceVariantId,
  )
  return match && match.url ? { variantId: match.id, url: match.url } : null
}

const errorText = (err: unknown, fallback: string) =>
  err instanceof Error ? err.message : fallback

/** The backend's current price, in cents, of one accepted background removal. */
const fetchRemovalPriceCents = async (): Promise<number> => {
  const pricing = await getCaseImageVariantPricing()
  const cents = pricing[CASE_IMAGE_VARIANT_KIND_KEY.BG_REMOVAL]
  if (typeof cents !== 'number') throw new Error('No background removal price')
  return cents
}

/**
 * Whether the variant is kept on the server, read again after an accept
 * whose answer was lost. A read that fails counts as not kept: the accept
 * stays on offer, and accepting a row the backend already keeps changes
 * nothing there, so sending it again cannot bill twice.
 */
const isVariantAccepted = async (variantId: string): Promise<boolean> => {
  try {
    const status = await getCaseImageVariantStatus(variantId)
    return status.accepted === true
  } catch {
    return false
  }
}

/**
 * State machine for the editor's Remove Background tool: the priced
 * disclosure or start screen, a polled generation, review with
 * accept/discard, and free re-apply of an earlier accepted result.
 *
 * Polling runs on timers held in refs rather than effects: each open()
 * bumps a generation token and every timer callback checks it, so closing
 * the tool or starting over cannot let a stale poll overwrite fresh state.
 * Accepting is the billable action and only ever happens from accept().
 */
export const useBackgroundRemoval = ({
  caseImageId,
  imageUrl,
  userId,
  appliedCutout = null,
  onApply,
  onAcceptError,
}: UseBackgroundRemovalArgs): UseBackgroundRemovalResult => {
  const [state, setState] = useState<BackgroundRemovalState>(initialState)
  const stateRef = useRef(state)
  stateRef.current = state

  // The callbacks below read these through refs so their identities stay
  // stable and no effect has to re-subscribe when the item changes.
  const caseImageIdRef = useRef(caseImageId)
  caseImageIdRef.current = caseImageId
  const imageUrlRef = useRef(imageUrl)
  imageUrlRef.current = imageUrl
  // The enhancement the cutout is made from, worked out on open() from the
  // image's variants; null means the base image. Kept for "Try again".
  const sourceVariantIdRef = useRef<string | null>(null)
  // Set when open() failed to look up the image's variants, so "Try again"
  // repeats the lookup (the source, the previous cutout and the disclosure)
  // instead of generating from a source it never worked out.
  const lookupFailedRef = useRef(false)
  const userIdRef = useRef(userId)
  userIdRef.current = userId
  // Written after commit, so a tab click never reads a cutout from a render
  // React discarded.
  const appliedCutoutRef = useRef(appliedCutout)
  useLayoutEffect(() => {
    appliedCutoutRef.current = appliedCutout
  }, [appliedCutout])

  const generationRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightRef = useRef(false)
  // The generation token of the accept call or item write that is pending,
  // if any. `stateRef` only changes on the next render, so two accepts from
  // the same render (a double click) would both see `review` and both bill;
  // this ref is set before the first awaits. It holds the token rather than a
  // flag so a call still pending from a closed lifecycle never blocks a new one.
  const acceptingRef = useRef<number | null>(null)
  // The pricing request whose answer the screen may show: bumped by every
  // open, retry, close and unmount. It is apart from the generation token so
  // that starting a removal does not drop the price the screen is quoting.
  const pricingTokenRef = useRef(0)

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  // Stop polling if the modal unmounts mid-generation.
  useEffect(
    () => () => {
      generationRef.current += 1
      pricingTokenRef.current += 1
      clearTimer()
    },
    [],
  )

  const update = (patch: Partial<BackgroundRemovalState>) =>
    setState((prev) => ({ ...prev, ...patch }))

  const fail = (errorMessage: string | null, disabled = false) => {
    inFlightRef.current = false
    update({ step: 'error', errorMessage, disabled })
  }

  const schedulePoll = (
    token: number,
    variantId: string,
    startedAt: number,
    consecutiveErrors: number,
  ) => {
    timerRef.current = setTimeout(async () => {
      if (token !== generationRef.current) return
      let errorsSoFar = consecutiveErrors
      try {
        const status = await getCaseImageVariantStatus(variantId)
        if (token !== generationRef.current) return
        errorsSoFar = 0
        if (status.status === CASE_IMAGE_JOB_STATUS_KEY.COMPLETED) {
          if (status.url) {
            inFlightRef.current = false
            update({
              step: 'review',
              result: { variantId, url: status.url },
              slow: false,
              errorMessage: null,
            })
            return
          }
          fail('Variant completed without an output url')
          return
        }
        if (status.status === CASE_IMAGE_JOB_STATUS_KEY.FAILED) {
          fail(
            status.errorCode
              ? `Variant failed: ${status.errorCode}`
              : 'Variant failed',
          )
          return
        }
      } catch (err) {
        if (token !== generationRef.current) return
        // A 4xx (variant deleted, session expired) is final. A network drop
        // or 5xx is not: the job keeps running server-side, so keep polling
        // unless the outage is sustained.
        if (
          !isTransientPollError(err) ||
          ++errorsSoFar >= MAX_CONSECUTIVE_POLL_ERRORS
        ) {
          fail(errorText(err, 'Status poll failed'))
          return
        }
      }
      const slow = Date.now() - startedAt >= BACKGROUND_REMOVAL_SLOW_AFTER_MS
      if (slow !== stateRef.current.slow) update({ slow })
      schedulePoll(token, variantId, startedAt, errorsSoFar)
    }, CASE_IMAGE_VARIANT_POLL_INTERVAL_MS)
  }

  const startGeneration = async () => {
    const currentCaseImageId = caseImageIdRef.current
    // A second click while a generation is running must not enqueue again.
    if (inFlightRef.current || !currentCaseImageId) return
    inFlightRef.current = true
    const token = ++generationRef.current
    clearTimer()
    update({
      step: 'removing',
      result: null,
      appliedPrevious: false,
      priceChanged: false,
      slow: false,
      errorMessage: null,
      disabled: false,
    })
    const startedAt = Date.now()
    try {
      const sourceVariantId = sourceVariantIdRef.current
      const { variantId } = await processCaseImageVariant({
        caseImageId: currentCaseImageId,
        kind: CASE_IMAGE_VARIANT_KIND_KEY.BG_REMOVAL,
        ...(sourceVariantId ? { sourceVariantId } : {}),
      })
      if (token !== generationRef.current) return
      update({ result: { variantId, url: '' } })
      schedulePoll(token, variantId, startedAt, 0)
    } catch (err) {
      if (token !== generationRef.current) return
      if (isAiImageEditsDisabledError(err)) {
        fail('AI image edits are switched off', true)
        return
      }
      fail(errorText(err, 'Failed to start generation'))
    }
  }

  // Asked on every open and every Try again, so a price an M3 admin changes
  // shows the next time the tool opens or starts over. Each open, and each
  // retry, starts with no price and shows only the answer to its own
  // request: a quote from an earlier open never fills a later one, so a
  // removal starts only at the price this open's request returned. A reply
  // for a request that has since been replaced is dropped whole.
  const loadPricing = async (token: number) => {
    try {
      const cents = await fetchRemovalPriceCents()
      if (token !== pricingTokenRef.current) return
      update({ priceCents: cents, priceError: false })
    } catch {
      // The screen shows no amount and cannot start a removal until the
      // price is known; the person can ask again or cancel.
      if (token === pricingTokenRef.current)
        update({ priceCents: null, priceError: true })
    }
  }

  const retryPricing = () => {
    update({ priceCents: null, priceError: false })
    void loadPricing(++pricingTokenRef.current)
  }

  const open = async () => {
    const currentCaseImageId = caseImageIdRef.current
    if (!currentCaseImageId) return
    const token = ++generationRef.current
    const pricingToken = ++pricingTokenRef.current
    clearTimer()
    inFlightRef.current = false
    lookupFailedRef.current = false
    sourceVariantIdRef.current = null

    // The item already shows a cutout: nothing to price or look up, only
    // the way back to the original photo to offer.
    const applied = appliedCutoutRef.current
    if (applied) {
      setState({
        ...initialState,
        step: 'applied',
        applied,
      })
      return
    }

    setState({ ...initialState, step: 'loading' })

    // Pricing is not awaited here: the start screen renders and the amount
    // fills in when it arrives; its start stays disabled until then.
    void loadPricing(pricingToken)

    let previous: BackgroundRemovalResult | null
    try {
      const variants = await getCaseImageVariantsForImage(currentCaseImageId)
      const sourceVariantId = sourceVariantForImage(
        variants,
        imageUrlRef.current,
      )
      if (token !== generationRef.current) return
      sourceVariantIdRef.current = sourceVariantId
      previous = findPreviousAcceptedCutout(variants, sourceVariantId)
    } catch (err) {
      if (token !== generationRef.current) return
      // An unknown image (404) or a dead backend: generating would fail the
      // same way, so say so now instead of starting a doomed job.
      lookupFailedRef.current = true
      fail(errorText(err, 'Failed to load previous results'))
      return
    }
    if (token !== generationRef.current) return

    if (previous) {
      update({ step: 'previous', previous })
      return
    }
    // A person who dismissed the disclosure skips its explanation, never the
    // price: every paid removal starts from a screen that shows it, as
    // Enhance's start and accept do.
    update({
      step: hasDismissedBackgroundRemovalDisclosure(userIdRef.current)
        ? 'start'
        : 'disclosure',
    })
  }

  const confirmStart = () => {
    const { step, priceCents } = stateRef.current
    if (step !== 'disclosure' && step !== 'start') return
    if (priceCents === null) return
    if (step === 'disclosure') {
      rememberBackgroundRemovalDisclosureDismissed(userIdRef.current)
    }
    void startGeneration()
  }

  /**
   * Write an accepted cutout onto the item, then show "accepted" only when
   * the item holds it. A failed write, thrown or reported, leaves the item
   * as it was and shows `applyFailed`, whose retry writes again for free.
   * Once the tool has closed or reopened, the write still runs (the person
   * accepted and was billed) but the tool's screens are left alone.
   */
  const applyToItem = async (
    token: number,
    result: BackgroundRemovalResult,
    appliedPrevious: boolean,
  ) => {
    if (token === generationRef.current)
      update({ step: 'applying', result, appliedPrevious })
    let written = false
    try {
      written = (await onApply(result)) === true
    } catch {
      written = false
    }
    if (token !== generationRef.current) return
    update({ step: written ? 'accepted' : 'applyFailed' })
  }

  const accept = async () => {
    const { result, step, priceCents } = stateRef.current
    const token = generationRef.current
    if (!result || step !== 'review' || acceptingRef.current === token) return
    // The accept control states the price accepting adds to the order;
    // with none on screen there is nothing to agree to.
    if (priceCents === null) return
    acceptingRef.current = token
    update({ step: 'accepting' })
    try {
      // The review quotes the price this open's pricing call answered, and an
      // M3 admin may have changed it since. Ask again and bill only at the
      // price on screen: a changed price bills nothing and is shown instead,
      // for the person to accept again at it. A person who closed the tool
      // while this was asked still gets the accept they pressed, at the price
      // they saw.
      let currentCents: number
      try {
        currentCents = await fetchRemovalPriceCents()
      } catch (err) {
        if (token !== generationRef.current) return
        onAcceptError?.(err)
        update({ step: 'review' })
        return
      }
      if (currentCents !== priceCents) {
        if (token !== generationRef.current) return
        // Drop any pricing answer still in flight, so it cannot replace the
        // amount this check found.
        pricingTokenRef.current += 1
        update({
          step: 'review',
          priceCents: currentCents,
          priceError: false,
          priceChanged: true,
        })
        return
      }

      try {
        await acceptCaseImageVariant(result.variantId)
      } catch (err) {
        if (isAiImageEditsDisabledError(err)) {
          if (token !== generationRef.current) return
          // Switched off since the tool opened: the accept was refused before
          // anything was written, so nothing is billed and accepting again
          // cannot help.
          fail('AI image edits are switched off', true)
          return
        }
        // A timeout, a dropped connection or a gateway error may hide an
        // accept that landed. The row is read again: a kept row is billed
        // already, so it goes on to the item like any accept, even when the
        // tool closed meanwhile, instead of showing a failure for a charge
        // that was made.
        if (
          mayHaveCommitted(err) &&
          (await isVariantAccepted(result.variantId))
        ) {
          await applyToItem(token, result, false)
          return
        }
        if (token !== generationRef.current) return
        onAcceptError?.(err)
        update({ step: 'review' })
        return
      }
      // Billed from here on: a failed write is retried, never re-accepted.
      // The result is written onto the item even when the tool was closed or
      // reopened while the accept was pending; applyToItem leaves the screen
      // of a lifecycle that has moved on alone.
      await applyToItem(token, result, false)
    } finally {
      if (acceptingRef.current === token) acceptingRef.current = null
    }
  }

  const discard = () => {
    if (stateRef.current.step !== 'review') return
    update({ step: 'discarded' })
  }

  // A failed generation goes back to the start screen rather than straight
  // into a new one, so the next paid removal starts, like the first, from a
  // screen that shows its price, asked again the way an open asks it.
  const tryAgain = () => {
    if (stateRef.current.step !== 'error' || stateRef.current.disabled) return
    if (lookupFailedRef.current) {
      lookupFailedRef.current = false
      void open()
      return
    }
    update({
      step: hasDismissedBackgroundRemovalDisclosure(userIdRef.current)
        ? 'start'
        : 'disclosure',
      result: null,
      errorMessage: null,
      slow: false,
      priceCents: null,
      priceError: false,
      priceChanged: false,
    })
    void loadPricing(++pricingTokenRef.current)
  }

  const applyPrevious = async () => {
    const { previous, step } = stateRef.current
    const token = generationRef.current
    if (!previous || step !== 'previous' || acceptingRef.current === token)
      return
    acceptingRef.current = token
    try {
      await applyToItem(token, previous, true)
    } finally {
      if (acceptingRef.current === token) acceptingRef.current = null
    }
  }

  const retryApply = async () => {
    const { result, step, appliedPrevious } = stateRef.current
    const token = generationRef.current
    if (!result || step !== 'applyFailed' || acceptingRef.current === token)
      return
    acceptingRef.current = token
    try {
      await applyToItem(token, result, appliedPrevious)
    } finally {
      if (acceptingRef.current === token) acceptingRef.current = null
    }
  }

  const close = () => {
    generationRef.current += 1
    pricingTokenRef.current += 1
    clearTimer()
    inFlightRef.current = false
    lookupFailedRef.current = false
    setState(initialState)
  }

  return {
    ...state,
    open,
    confirmStart,
    retryPricing,
    accept,
    discard,
    tryAgain,
    applyPrevious,
    retryApply,
    close,
  }
}
